import { useState } from 'react'
import { motion } from 'framer-motion'
import { Star, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'
import { useGoogleReviews } from '@/hooks/useGoogleReviews'
import type { GoogleReview } from '@/types/review.types'

interface TestimonialsSectionProps {
  prefersReducedMotion?: boolean
}

// Google Maps page URL for "View on Google" link
const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/AcV6Wq24J44b4GxT6'

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex text-garage-accent mb-3">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating ? 'fill-current' : 'fill-none opacity-30'
          }`}
        />
      ))}
    </div>
  )
}

function ReviewCard({
  review,
  index,
  prefersReducedMotion,
}: {
  review: GoogleReview
  index: number
  prefersReducedMotion: boolean
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  // Only show expand button for longer reviews (more than ~150 chars)
  const isLongReview = review.text.length > 150

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        delay: prefersReducedMotion ? 0 : index * 0.1,
        duration: prefersReducedMotion ? 0.4 : 0.6,
      }}
      className="bg-garage-card rounded-lg p-6 shadow-garage border border-garage-border hover:border-garage-accent transition-all duration-150"
    >
      <div className="flex items-center mb-4">
        {review.author_photo_url ? (
          <img
            src={review.author_photo_url}
            alt={`${review.author_name} review`}
            className="w-12 h-12 rounded-full mr-4 object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-12 h-12 rounded-full mr-4 bg-garage-accent/20 flex items-center justify-center">
            <span className="text-garage-accent font-bold text-lg">
              {review.author_name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <div>
          <h4 className="font-semibold text-garage-dark font-garage-title">
            {review.author_name}
          </h4>
          <p className="text-sm text-garage-text/70">
            {review.relative_time_description}
          </p>
        </div>
      </div>
      <StarRating rating={review.rating} />
      <p
        className={`text-garage-text italic ${
          isExpanded ? '' : 'line-clamp-4'
        }`}
      >
        "{review.text}"
      </p>
      {isLongReview && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 flex items-center gap-1 text-sm text-garage-accent hover:text-garage-primary transition-colors font-medium"
        >
          {isExpanded ? (
            <>
              <span>Show less</span>
              <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              <span>Read more</span>
              <ChevronDown className="w-4 h-4" />
            </>
          )}
        </button>
      )}
    </motion.div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="grid md:grid-cols-3 gap-8">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-garage-card rounded-lg p-6 shadow-garage border border-garage-border animate-pulse"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-full mr-4 bg-garage-border" />
            <div className="flex-1">
              <div className="h-4 bg-garage-border rounded w-24 mb-2" />
              <div className="h-3 bg-garage-border rounded w-16" />
            </div>
          </div>
          <div className="flex gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="w-4 h-4 bg-garage-border rounded" />
            ))}
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-garage-border rounded w-full" />
            <div className="h-3 bg-garage-border rounded w-full" />
            <div className="h-3 bg-garage-border rounded w-3/4" />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function TestimonialsSection({
  prefersReducedMotion = false,
}: TestimonialsSectionProps) {
  const { data, isLoading } = useGoogleReviews()

  const reviews = data?.reviews || []
  // Use fallback if summary is missing or has 0 reviews (database cache issue)
  const summary =
    data?.summary && data.summary.total_reviews > 0
      ? data.summary
      : { average_rating: 5.0, total_reviews: 10 }
  // Only show 5-star reviews, limit to 4
  const fiveStarReviews = reviews.filter((r) => r.rating === 5)
  const displayedReviews = fiveStarReviews.slice(0, 4)

  // Calculate positive review percentage (4-5 stars = positive)
  const positivePercentage =
    reviews.length > 0
      ? Math.round(
          (reviews.filter((r) => r.rating >= 4).length / reviews.length) * 100
        )
      : 100

  return (
    <section className="py-20 bg-garage-surface relative overflow-hidden">
      {/* Patina Texture */}
      <div className="absolute inset-0 bg-garage-patina-texture opacity-5" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: prefersReducedMotion ? 0.2 : 0.4 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-garage-dark mb-4 font-garage-title">
            What <span className="text-garage-accent">Riders Say</span>
          </h2>
          <p className="text-lg text-garage-text max-w-2xl mx-auto">
            Real adventures from real riders who discovered{' '}
            <strong>Laos electric enduro</strong> with us — from{' '}
            <strong>backpacker adventure tours</strong> to professional{' '}
            <strong>off-road experiences</strong> on our Kayo EC2i bikes
          </p>
        </motion.div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <div
            className={`grid gap-6 ${
              displayedReviews.length === 4
                ? 'md:grid-cols-2 lg:grid-cols-4'
                : displayedReviews.length === 2
                ? 'md:grid-cols-2 max-w-3xl mx-auto'
                : 'md:grid-cols-3'
            }`}
          >
            {displayedReviews.map((review, index) => (
              <ReviewCard
                key={review.author_name + index}
                review={review}
                index={index}
                prefersReducedMotion={prefersReducedMotion}
              />
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: prefersReducedMotion ? 0 : 0.2, duration: 0.3 }}
          className="text-center mt-12 space-y-4"
        >
          {/* Rating Badge */}
          <div className="inline-flex items-center bg-garage-green/20 text-garage-green px-4 py-2 rounded-full text-sm font-medium border border-garage-green/30">
            <span className="mr-2">🏆</span>
            {summary.average_rating}/5 Rating • {summary.total_reviews}+ Reviews
            • {positivePercentage}% Positive
          </div>

          {/* Google Reviews Link */}
          <div>
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-garage-accent hover:text-garage-primary transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span>View all reviews on Google</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
